(() => {
  if (window.__cartBackorderPropsInit) return;
  window.__cartBackorderPropsInit = true;

  const PROP_KEY = "_accepted-customer-agreement";
  const PREORDER_KEY = "Pre-order";
  const BACKORDER_KEY = "Back-order";
  const SHIPPING_PROP_KEYS = ["Ships between", "Ships in", "Shipping between"];
  const TRIGGER_RE = /\/cart\/(add|update)(\.js|\.json)?(\?|$)/i;

  const messageText = (kind) =>
    window.backorderPropsData?.[kind === "preorder" ? "preorderText" : "backorderText"] ?? "";

  const toUsDate = (iso) => {
    const [y, m, d] = String(iso).split("-");
    return y && m && d ? `${m}/${d}/${y}` : String(iso);
  };

  const readableMessage = (kind, iso) => {
    const text = messageText(kind);
    return `${text} ${toUsDate(iso)}`.trim();
  };

  const nativeFetch =
    typeof window.fetch === "function" ? window.fetch.bind(window) : null;

  const productDataCache = new Map();
  const productDataPending = new Map();

  let running = false;
  let queued = false;

  const isTriggerUrl = (url) => typeof url === "string" && TRIGGER_RE.test(url);

  const parseVariantDateAttr = (attr, variantId) => {
    if (!attr) return null;
    for (const token of attr.split(",")) {
      const [id, date] = token.split(":");
      if (id && date && parseInt(id, 10) === parseInt(variantId, 10)) {
        return date.trim();
      }
    }
    return null;
  };

  const listsVariant = (attr, variantId) =>
    (attr ?? "")
      .split(",")
      .some((id) => id && parseInt(id, 10) === parseInt(variantId, 10));

  const shippingFromDom = (el, variantId) => {
    const option = el.querySelector(
      `option[value="${parseInt(variantId, 10)}"][data-shipping-properties]`
    );
    try {
      return JSON.parse(option?.dataset.shippingProperties || "null");
    } catch {
      return null;
    }
  };

  const infoFromDom = (variantId) => {
    const holders = document.querySelectorAll(
      "[data-variants-backorder], [data-variants-preorder], [data-variant-ids]"
    );
    for (const el of holders) {
      const backorder = parseVariantDateAttr(el.dataset.variantsBackorder, variantId);
      const preorder = parseVariantDateAttr(el.dataset.variantsPreorder, variantId);
      if (backorder || preorder || listsVariant(el.dataset.variantIds, variantId)) {
        return {
          backorder,
          preorder,
          made_to_order: el.dataset.madeToOrder === "true",
          returns: el.dataset.return === "true",
          shipping: shippingFromDom(el, variantId),
        };
      }
    }
    return null;
  };

  const getProductData = (handle) => {
    if (!handle || !nativeFetch) return Promise.resolve(null);
    if (productDataCache.has(handle)) {
      return Promise.resolve(productDataCache.get(handle));
    }
    if (productDataPending.has(handle)) return productDataPending.get(handle);

    const promise = nativeFetch(
      `/products/${encodeURIComponent(handle)}?view=backorder`,
      { credentials: "same-origin" }
    )
      .then((res) => (res.ok ? res.text() : null))
      .then((text) => {
        productDataPending.delete(handle);
        if (!text) return null;
        try {
          const parsed = JSON.parse(text);
          const data = { variants: parsed?.variants ?? null };
          productDataCache.set(handle, data);
          return data;
        } catch {
          return null;
        }
      })
      .catch(() => {
        productDataPending.delete(handle);
        return null;
      });

    productDataPending.set(handle, promise);
    return promise;
  };

  const OWNED_TOKENS = [
    "backorder_agreement",
    "preorder_agreement",
    "made_to_order_agreement",
    "return_agreement",
  ];

  const isOwnedToken = (token) =>
    OWNED_TOKENS.includes(token) ||
    /\|(backorder|preorder)_agreement$/.test(token);

  // Keep this order, Celigo parses the dated slots by position.
  const buildDesiredValue = (currentValue, info) => {
    const kept = (currentValue ?? "")
      .replace(/^\[/, "")
      .replace(/\]$/, "")
      .split(",")
      .map((token) => token.trim())
      .filter(Boolean)
      .filter((token) => !isOwnedToken(token));

    const rebuilt = [];
    if (info?.backorder) rebuilt.push(`|${info.backorder}|backorder_agreement`);
    if (info?.preorder) rebuilt.push(`|${info.preorder}|preorder_agreement`);
    if (info?.made_to_order) rebuilt.push("made_to_order_agreement");
    if (info?.returns) rebuilt.push("return_agreement");

    const all = [...rebuilt, ...kept];
    const desired = all.length ? `[${all.join(",")}]` : "";

    if (!desired && !currentValue) return null;
    if (desired === currentValue) return null;
    return desired;
  };

  const updateLineProperties = (lineKey, properties) => {
    if (!nativeFetch) return Promise.resolve(false);
    return nativeFetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: lineKey, properties }),
    })
      .then((res) => res.ok)
      .catch(() => false);
  };

  const applyShippingProperties = (props, shipping) => {
    if (!shipping || !Object.keys(shipping).length) return false;
    let changed = false;
    for (const key of SHIPPING_PROP_KEYS) {
      if (shipping[key]) {
        if ((props[key] ?? "").trim() !== shipping[key]) {
          props[key] = shipping[key];
          changed = true;
        }
      } else if (key in props) {
        delete props[key];
        changed = true;
      }
    }
    return changed;
  };

  const desiredMessage = (currentValue, wantedValue) => {
    if (wantedValue) return wantedValue === currentValue ? undefined : wantedValue;
    return currentValue ? "" : undefined;
  };

  const reconcileLine = async (item) => {
    const data = await getProductData(item.handle);
    const info =
      infoFromDom(item.variant_id) ??
      data?.variants?.[String(item.variant_id)] ??
      null;
    if (!info && !data) return false;

    const props = item.properties ?? {};
    const nextProps = { ...props };
    let changed = false;

    const agreement = buildDesiredValue(props[PROP_KEY], info);
    if (agreement !== null) {
      nextProps[PROP_KEY] = agreement;
      changed = true;
    }

    const preValue = info?.preorder ? readableMessage("preorder", info.preorder) : null;
    const preDesired = desiredMessage(props[PREORDER_KEY], preValue);
    if (preDesired !== undefined) {
      nextProps[PREORDER_KEY] = preDesired;
      changed = true;
    }

    const backValue = info?.backorder ? readableMessage("backorder", info.backorder) : null;
    const backDesired = desiredMessage(props[BACKORDER_KEY], backValue);
    if (backDesired !== undefined) {
      nextProps[BACKORDER_KEY] = backDesired;
      changed = true;
    }

    const shipping =
      data?.variants?.[String(item.variant_id)]?.shipping ?? info?.shipping;
    if (applyShippingProperties(nextProps, shipping)) changed = true;

    if (!changed) return false;
    return updateLineProperties(item.key, nextProps);
  };

  const reconcileCart = async () => {
    if (!nativeFetch) return;
    if (running) {
      queued = true;
      return;
    }
    running = true;
    let patched = false;

    try {
      const res = await nativeFetch("/cart.js", { credentials: "same-origin" });
      const cart = res.ok ? await res.json() : null;
      for (const item of cart?.items ?? []) {
        if (await reconcileLine(item)) patched = true;
      }
    } catch (error) {
      console.debug("[backorder-props] reconcile failed", error);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        reconcileCart();
      } else if (patched) {
        document.dispatchEvent(
          new CustomEvent("dispatch:cart-drawer:refresh", { bubbles: true })
        );
      }
    }
  };

  if (typeof window.fetch === "function") {
    const prevFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === "string" ? input : input?.url;
      const result = prevFetch.apply(this, arguments);
      if (isTriggerUrl(url)) result.then(() => reconcileCart(), () => {});
      return result;
    };
  }

  const { XMLHttpRequest: XHR } = window;
  if (XHR?.prototype) {
    const { open: nativeOpen, send: nativeSend } = XHR.prototype;

    XHR.prototype.open = function (method, url) {
      this.__backorderUrl = url;
      return nativeOpen.apply(this, arguments);
    };

    XHR.prototype.send = function () {
      if (isTriggerUrl(this.__backorderUrl)) {
        this.addEventListener("load", function () {
          if (this.status >= 200 && this.status < 300) reconcileCart();
        });
      }
      return nativeSend.apply(this, arguments);
    };
  }
})();
